import React from 'react'
import { connect } from 'react-redux'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import { media, Container } from '../utils/style'
import { Box, Flex, Text } from 'rebass'
import ContentBrandCtaBar from '../components/ContentBrandCtaBar'
import ContentSection from '../components/ContentSection'
import citygateTheme from '../theme/'
import ContentSectionHero from '../components/ContentSectionHero'
import ContentDealershipLocationDetails from '../components/ContentDealershipLocationDetails'
import ContactForm from '../components/ContentContactForm'
import LiveChat from '../components/LiveChat'
import { setGlobalNumber } from '../state/phone-number/actions'
import Layout from '../components/Layout'
import buildImageUrl from '../../lib/media-url-maker'
import renderWidgets from '../../lib/widget-renderer.jsx'

const FlexWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 100%;
`

const DealershipDescription = styled(Box)`
  position: relative;
  max-width: 85%;
  ${media.MediumScreenUp`
  max-width: 60%;
  `};
  text-align: center;
  margin: 4vh auto 14vh;
  line-height: 1.7;
`

const DealershipHeader = styled(ContentSectionHero)`
  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.48;
    background-image: linear-gradient(
      -90deg,
      rgba(0, 0, 0, 0) 25%,
      #001130 77%
    );
    z-index: -1;
  }
`

const ContactAnchor = styled.a`
  display: block;
  width: 100%;
  height: 0;
  overflow: hidden;
`

const formatAddress = (addressLines) => {
  let address = ''
  addressLines.forEach((line, index) => {
    let addressLine = line
    if (index !== addressLines.length - 1)
      addressLine = addressLine.concat(', ')
    address = address.concat(` ${addressLine}`)
  })
  return address
}

const cleanPhoneNumber = (number) => number.replace(/[^\d]/g, '')

class Dealership extends React.Component {
  render() {
    const dealership =
      this.props.pageContext.dealership && this.props.pageContext.dealership
    const ctaItems = [
      {
        label: (
          <span>
            Call us{' '}
            {dealership.calltracksNumber || dealership.serviceBookingTelephone}
          </span>
        ),
        href: `tel:${cleanPhoneNumber(
          dealership.calltracksNumber || dealership.serviceBookingTelephone
        )}`,
        eventCategory: 'DealershipServiceBooking'
      },
      {
        label: <span>Contact us</span>,
        to: `contact-us`,
        animateScroll: true
      }
    ]
    const sectionTitle = dealership.shortName
    const usedVehicles =
      this.props.data.allUsedVehicleCatfish &&
      this.props.data.allUsedVehicleCatfish.edges.map(({ node }) => node)
    const makes =
      this.props.data.allMakeCatfish &&
      this.props.data.allMakeCatfish.edges.map(({ node }) => node)
    const allModels =
      this.props.data.allModelCatfish &&
      this.props.data.allModelCatfish.edges.map(({ node }) => node)
    const allBodyTypes =
      this.props.data.allBodyTypeCatfish &&
      this.props.data.allBodyTypeCatfish.edges.map(({ node }) => node)

    let addressLines = [
      dealership.addressLine1,
      dealership.addressLine2,
      dealership.addressLine3,
      dealership.town,
      dealership.county,
      dealership.postcode
    ]
    addressLines = addressLines.filter((line) => line) // Filter out empty fields
    const address = formatAddress(addressLines)
    return (
      <Layout location={this.props.location}>
        <Box bg="#EEF4F9">
          <LiveChat providerId={dealership.liveChatProviderId} />
          <DealershipHeader
            title={dealership.location}
            bg={{
              color: '#0A0F17',
              x1: buildImageUrl({
                resource: dealership.images[0],
                width: 1440
              }),
              x2: buildImageUrl({ resource: dealership.images[0] })
            }}
            reversed
            connect
            alignment={'left'}
            relaxed
          >
            <Container pr={['50%', '35%']} ml={'-16px'}>
              <Text f={[3, 4, 5]} mb={1}>
                {address}
              </Text>
            </Container>
          </DealershipHeader>
          <FlexWrapper flexWrap="wrap">
            <ContentBrandCtaBar items={ctaItems} duotone removemargin />
            <ContentSection
              title={sectionTitle}
              right
              bg="#EEF4F9"
              fg="#F5F9FB"
              opacity={1}
            >
              <DealershipDescription>
                <div
                  dangerouslySetInnerHTML={{ __html: dealership.description }}
                />
              </DealershipDescription>
              <ContentDealershipLocationDetails dealership={dealership} />
            </ContentSection>

            {dealership.body.length > 0 && (
              <FlexWrapper bg={citygateTheme.colors.white} flexWrap="wrap">
                <Box w="100%">
                  {renderWidgets({
                    widgets: dealership.body,
                    pageType: 'section',
                    data: this.props.data,
                    usedVehicles,
                    makes,
                    allModels,
                    allBodyTypes
                  })}
                </Box>
              </FlexWrapper>
            )}

            <ContactAnchor name="contact-us" />
            <ContactForm
              heading="Committed to Excellence"
              title={`${dealership.location}`}
              locations={[dealership.shortName]}
              locationRequired={false}
              registrationRequired={false}
            >
              <p>
                At Citygate, our commitment to you is to maintain the highest
                possible dealership standards at all times. Whether you’re
                searching for a new vehicle, one of our used vehicles or having
                your vehicle serviced with us, we pride ourselves on our
                excellent customer service, knowledge and expertise to assist
                you in every possible way. If you have a question, fill in our
                form and one of our Citygate team members will be in touch with
                you.
              </p>
            </ContactForm>
          </FlexWrapper>
        </Box>
      </Layout>
    )
  }
}

const mapDispatchToProps = (dispatch) => ({
  setGlobalNumber: (settings) => dispatch(setGlobalNumber(settings))
})

export default connect(null, mapDispatchToProps)(Dealership)

export const pageQuery = graphql`
  query DealershipQuery {
    allUsedVehicleCatfish {
      edges {
        node {
          ...usedVehicleSummaryFields
          dealership {
            _id
          }
        }
      }
    }
    allMakeCatfish(
      filter: { state: { eq: "Published" } }
      sort: { order: ASC }
    ) {
      totalCount
      edges {
        node {
          ...makeSummary
        }
      }
    }
    allMakeCatfish(
      filter: { state: { eq: "Published" } }
      sort: { order: ASC }
    ) {
      totalCount
      edges {
        node {
          ...makeSummary
        }
      }
    }
    allModelCatfish(filter: { state: { eq: "Published" } }) {
      totalCount
      edges {
        node {
          ...modelSummary
        }
      }
    }
    allBodyTypeCatfish {
      edges {
        node {
          id: _id
          name
        }
      }
    }
  }
`
